/**
 * Production Operational Telemetry & Structured Logging Service
 * Tracks client/server errors, API latencies, Firestore transaction metrics, and email failures.
 */

export type LogLevel = 'info' | 'warn' | 'error' | 'security';

export interface TelemetryEvent {
  timestamp: string;
  level: LogLevel;
  category: 'api' | 'auth' | 'reservation' | 'inventory' | 'email' | 'system';
  message: string;
  durationMs?: number;
  metadata?: Record<string, any>;
  error?: {
    message: string;
    stack?: string;
    code?: string | number;
  };
}

class TelemetryService {
  private inMemoryLogs: TelemetryEvent[] = [];
  private readonly MAX_IN_MEMORY_LOGS = 100;

  /**
   * Records a structured telemetry event
   */
  public log(event: Omit<TelemetryEvent, 'timestamp'>): void {
    const fullEvent: TelemetryEvent = {
      ...event,
      timestamp: new Date().toISOString()
    };

    // Keep ring buffer in memory for UI diagnostics
    this.inMemoryLogs.unshift(fullEvent);
    if (this.inMemoryLogs.length > this.MAX_IN_MEMORY_LOGS) {
      this.inMemoryLogs.pop();
    }

    // Format console output with category tags
    const prefix = `[UniformLoop:${event.category.toUpperCase()}]`;
    if (event.level === 'error' || event.level === 'security') {
      console.error(prefix, event.message, event.error || event.metadata || '');
    } else if (event.level === 'warn') {
      console.warn(prefix, event.message, event.metadata || '');
    } else {
      console.info(prefix, event.message, event.durationMs ? `(${event.durationMs}ms)` : '');
    }
  }

  /**
   * Helper to time async operations and log performance metrics
   */
  public async time<T>(
    category: TelemetryEvent['category'],
    operationName: string,
    fn: () => Promise<T>,
    metadata?: Record<string, any>
  ): Promise<T> {
    const start = performance.now();
    try {
      const result = await fn();
      const durationMs = Math.round(performance.now() - start);
      this.log({
        level: 'info',
        category,
        message: `${operationName} completed successfully`,
        durationMs,
        metadata
      });
      return result;
    } catch (err: any) {
      const durationMs = Math.round(performance.now() - start);
      this.log({
        level: 'error',
        category,
        message: `${operationName} failed: ${err.message}`,
        durationMs,
        metadata,
        error: {
          message: err.message,
          stack: err.stack,
          code: err.code || err.status
        }
      });
      throw err;
    }
  }

  /**
   * Returns recent in-memory telemetry logs for admin diagnostics view
   */
  public getRecentLogs(): TelemetryEvent[] {
    return [...this.inMemoryLogs];
  }

  /**
   * Clears in-memory log buffer
   */
  public clearLogs(): void {
    this.inMemoryLogs = [];
  }
}

export const telemetry = new TelemetryService();
